/**
 * Single source of truth for all user roles in CourtSync.
 *
 * All role types, Zod schemas, and role-checking utilities derive from these constants.
 * Add new roles here and they will propagate automatically.
 */

/** All valid roles in the platform (matches user_roles.role CHECK constraint). */
export const USER_ROLES = [
  'admin',
  'academy_owner',
  'academy_admin',
  'club_owner',
  'club_admin',
  'coach',
  'guardian',
  'student',
  'member',
] as const;

/** Org-scoped authority roles (organization_members.role CHECK constraint). */
export const ORGANIZATION_ROLES = ['org_owner', 'org_admin'] as const;

/** Derived union type from ORGANIZATION_ROLES. */
export type OrganizationRole = (typeof ORGANIZATION_ROLES)[number];

/** Derived union type from USER_ROLES. Use this instead of writing the union manually. */
export type UserRole = (typeof USER_ROLES)[number];

/**
 * Roles that can be explicitly assigned via the role update API.
 * 'student' is included — it can be assigned implicitly via booking or explicitly by an admin.
 */
export const ASSIGNABLE_ROLES = USER_ROLES;
export type AssignableRole = UserRole;

/**
 * Role priority order for picking a primary role when a user holds multiple.
 * Higher index = lower priority.
 */
export const ROLE_PRIORITY_ORDER = [
  'admin',
  'academy_owner',
  'academy_admin',
  'club_owner',
  'club_admin',
  'coach',
  'guardian',
  'student',
  'member',
] as const satisfies readonly UserRole[];
